import type * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { CenteredDialog } from "./CenteredDialog";

export interface AlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  cancelText?: string;
  confirmText?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  variant?: "danger" | "primary";
  loading?: boolean;
  loadingText?: string;
  /** false로 설정하면 확인 버튼 클릭 시 자동으로 닫히지 않음 (기본: true) */
  closeOnConfirm?: boolean;
  /** 확인 문구와 버튼 사이 추가 입력 슬롯 (예: 상태 변경 메모) */
  children?: React.ReactNode;
}

/**
 * 경고/확인 다이얼로그 컴포넌트
 * - variant="danger": 빨간색 확인 버튼 (나가기, 삭제 등)
 * - variant="primary": 녹색 확인 버튼 (등록, 저장 등)
 */
export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  cancelText,
  confirmText,
  onCancel,
  onConfirm,
  variant = "primary",
  loading = false,
  loadingText,
  closeOnConfirm = true,
  children,
}: AlertDialogProps) {
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    onConfirm?.();
    if (closeOnConfirm) onOpenChange(false);
  };

  return (
    <CenteredDialog open={open} onOpenChange={onOpenChange}>
      <div className="flex flex-col gap-5 py-6">
        {/* Content */}
        <div className="flex flex-col gap-3.25 px-6">
          <DialogPrimitive.Title className="typo-heading-2-semibold text-labels-primary whitespace-pre-line">
            {title}
          </DialogPrimitive.Title>
          {description ? (
            <DialogPrimitive.Description className="typo-body-2-medium text-labels-alternative">
              {description}
            </DialogPrimitive.Description>
          ) : (
            <DialogPrimitive.Description className="hidden" />
          )}
        </div>

        {children ? <div className="px-6">{children}</div> : null}

        {/* Buttons */}
        <div className="flex items-center gap-2 pt-2 px-6">
          {/* danger variant: 확인(빨간) → 취소 순서 */}
          {variant === "danger" && confirmText && (
            <button
              type="button"
              disabled={loading}
              onClick={handleConfirm}
              className={cn(
                "flex-1 flex items-center justify-center",
                "px-4.5 py-3 rounded-xl",
                "typo-body-1-semibold",
                "inset-border-double",
                "relative overflow-hidden",
                loading
                  ? "bg-bg-disabled text-labels-assistive cursor-not-allowed"
                  : [
                      "bg-status-urgent text-static-white",
                      "after:absolute after:inset-0 after:bg-static-black",
                      "after:opacity-0 hover:after:opacity-[0.06]",
                      "after:transition-opacity",
                    ],
              )}
            >
              <span className="relative z-10">
                {loading ? (loadingText ?? confirmText) : confirmText}
              </span>
            </button>
          )}

          {/* 취소 버튼 (outline 스타일) */}
          {cancelText && (
            <button
              type="button"
              onClick={handleCancel}
              className={cn(
                "flex-1 flex items-center justify-center",
                "px-4.5 py-3 rounded-xl",
                "bg-static-white text-labels-primary",
                "typo-body-1-semibold",
                "inset-outline-strong",
                "relative overflow-hidden",
                "after:absolute after:inset-0 after:bg-static-black",
                "after:opacity-0 hover:after:opacity-[0.06]",
                "after:transition-opacity",
              )}
            >
              <span className="relative z-10">{cancelText}</span>
            </button>
          )}

          {/* primary variant: 취소 → 확인(녹색) 순서 */}
          {variant !== "danger" && confirmText && (
            <button
              type="button"
              disabled={loading}
              onClick={handleConfirm}
              className={cn(
                "flex-1 flex items-center justify-center",
                "px-4.5 py-3 rounded-xl",
                "typo-body-1-semibold",
                "inset-border-double",
                "relative overflow-hidden",
                loading
                  ? "bg-bg-disabled text-labels-assistive cursor-not-allowed"
                  : [
                      "bg-primary-normal text-static-white",
                      "after:absolute after:inset-0 after:bg-static-black",
                      "after:opacity-0 hover:after:opacity-[0.06]",
                      "after:transition-opacity",
                    ],
              )}
            >
              <span className="relative z-10">
                {loading ? (loadingText ?? confirmText) : confirmText}
              </span>
            </button>
          )}
        </div>
      </div>
    </CenteredDialog>
  );
}

AlertDialog.displayName = "AlertDialog";
